import React, { useState, useCallback } from "react";

import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  RoomFormModal,
  RoomsTable,
  VenueSelector,
} from "@/components/features";
import { useRooms } from "@/hooks";
import { useApp, useVenuesContext } from "@/contexts";
import { Room } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const RoomsPage: React.FC<PageProps> = (props: PageProps) => {
  const { user } = useApp();
  //const navigate = useNavigate();
  const {
    selectedVenue,
    loading: loadingVenues,
    setSelectedVenue,
  } = useVenuesContext();

  // Local search state
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Use the rooms hook for all data management
  const {
    // Data state
    rooms,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingRoomId,
    selectedRoom,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    //setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedRoom,

    // Error handling
    lastError,
    clearError,
  } = useRooms({
    venueId: selectedVenue?.id || "",
    pageSize: 20,
    autoLoad: true,
    userId: user?.id,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [roomToDelete, setRoomToDelete] = useState<Room | null>(null);

  // Handle search with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      // Apply search filter
      const newFilters = { ...filters, searchKeyword: value.trim() };

      // Update filters and reload data
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, list, sortBy]
  );

  // Handle add room button click
  const handleAddRoom = useCallback(() => {
    setSelectedRoom(null);
    setFormModalOpened(true);
  }, [setSelectedRoom]);

  // Handle edit room
  const handleEditRoom = useCallback(
    (room: Room) => {
      setSelectedRoom(room);
      setFormModalOpened(true);
    },
    [setSelectedRoom]
  );

  const handleDuplicateRoom = useCallback(
    (room: Room) => {
      setSelectedRoom({ ...room, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedRoom]
  );

  // Handle view room (could navigate to room details page)
  const handleViewRoom = useCallback((room: Room) => {
    // TODO: Navigate to room details page or show room details modal
    console.log("View room:", room);
  }, []);

  // Handle remove room button click
  const handleRemoveRoomClick = useCallback((room: Room) => {
    setRoomToDelete(room);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (roomToDelete?.id) {
      const success = await remove(roomToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setRoomToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [roomToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setRoomToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedRoom(null);
    clearError();
  }, [setSelectedRoom, clearError]);

  // Handle venue selection change
  const handleVenueChange = useCallback(
    (venueId: string | null) => {
      if (venueId && selectedVenue?.id !== venueId) {
        // Find and set the venue object - this is a simplified approach
        // In a real app, you might want to fetch the venue object by ID
        // For now, we'll just update with the ID and let the context handle it
        setSelectedVenue({ id: venueId } as any);

        const newFilters = { ...filters, venueId: [venueId] };

        // Update filters and reload data
        list(
          newFilters,
          { page: 1, pageSize: DEFAULT_PAGE_SIZE },
          sortBy,
          true
        );
      } else if (!venueId) {
        setSelectedVenue(null);
      }
    },
    [selectedVenue?.id, filters, setSelectedVenue]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle room creation
  const handleCreateRoom = useCallback(
    async (roomData: any) => {
      const result = await create(roomData);
      if (result) {
        setFormModalOpened(false);
        setSelectedRoom(null);
      }
      return result;
    },
    [create, setSelectedRoom]
  );

  // Handle room update
  const handleUpdateRoom = useCallback(
    async (roomId: string, roomData: any) => {
      const result = await update(roomId, roomData);
      if (result) {
        setFormModalOpened(false);
        setSelectedRoom(null);
      }
      return result;
    },
    [update, setSelectedRoom]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Actions toolbar with add/export buttons and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <VenueSelector
                  key="venue-selector"
                  value={selectedVenue?.id || null}
                  onChange={handleVenueChange}
                  placeholder="Select venue to manage displays"
                  style={{ minWidth: 250 }}
                  required
                />,
                <AppButton
                  key="add-room"
                  iconName="Plus"
                  onClick={handleAddRoom}
                  disabled={isCreating || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search rooms..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Rooms table */}
        <RoomsTable
          rooms={rooms}
          loading={loading || loadingVenues}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditRoom={handleEditRoom}
          onDuplicateRoom={handleDuplicateRoom}
          onRemoveRoom={handleRemoveRoomClick}
          onViewRoom={handleViewRoom}
          removingRoomId={removingRoomId}
          size="md"
        />

        <EmptyState
          display={isEmpty && !loading && !loadingVenues}
          title="No rooms yet"
          description="Start by adding your first room to this venue"
          actionIcon="Plus"
          actionLabel="Add Room"
          onAction={handleAddRoom}
        />
      </Stack>

      {/* Room form modal for create/edit */}
      <RoomFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        room={selectedRoom}
        venueId={selectedVenue?.id}
        onCreateRoom={handleCreateRoom}
        onUpdateRoom={handleUpdateRoom}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Room"
        message={`Are you sure you want to delete "${roomToDelete?.name}"?`}
        details="This action cannot be undone. All associated bookings will also be deleted."
        confirmInProgress={removingRoomId === roomToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
